import Resource from './Resource';
import MediaList, { TMedia } from './MediaList';
import Audio from './Audio';
import Video from './Video';
import AdaptrFeedFm from './AdaptrFeedFm';
import WaveSurfer from './WaveSurfer';

class MediaPlayer {
  private globalPause: Record<string, boolean> = {};

  private globalMute: Record<string, boolean> = {};

  private mediaList = new MediaList();

  private stopListener: Record<string, any> = {};

  public play = (id: string | undefined): TMedia | undefined => {
    if (!Resource || !id) {
      return undefined;
    }
    const resource = Resource.get(id);
    const target = resource?.object;
    if (!(target instanceof Audio || target instanceof AdaptrFeedFm || target instanceof Video || target instanceof WaveSurfer)) {
      return undefined;
    }
    if (this.mediaList.exists(target)) {
      target.play();
      return undefined;
    }
    if (target instanceof Audio && !target.isLooping()) {
      this.stopListener[id] = this.stop.bind(this, id);
      target.addEventListenerOnce(Audio.EVENT.END, this.stopListener[id]);
    }
    if (target instanceof AdaptrFeedFm && !target.isLooping()) {
      this.stopListener[id] = this.stop.bind(this, id);
      target.addEventListenerOnce(AdaptrFeedFm.EVENT.END, this.stopListener[id]);
    }
    if (target instanceof Video && !target.isLooping()) {
      this.stopListener[id] = this.stop.bind(this, id);
      target.addEventListenerOnce(Video.EVENT.ENDED, this.stopListener[id]);
    }
    if (target instanceof WaveSurfer && !target.isLooping()) {
      this.stopListener[id] = this.stop.bind(this, id);
      target.addEventListenerOnce(WaveSurfer.EVENT.FINISH, this.stopListener[id]);
    }
    target.play();
    this.mediaList.add(target);
    this.volumeNormalize();
    return target;
  };

  public stop = (id:string | undefined): TMedia | undefined => {
    if (!Resource || !id) {
      return undefined;
    }
    const resource = Resource.get(id);
    const target = resource?.object;
    if (!(target instanceof Audio || target instanceof AdaptrFeedFm || target instanceof Video || target instanceof WaveSurfer)) {
      return undefined;
    }
    if (!this.mediaList.exists(target)) {
      return undefined;
    }
    if (target instanceof Audio && !target.isLooping() && this.stopListener[id]) {
      target.removeEventListener(Audio.EVENT.END, this.stopListener[id]);
      delete this.stopListener[id];
    }
    if (target instanceof AdaptrFeedFm && !target.isLooping() && this.stopListener[id]) {
      target.removeEventListener(AdaptrFeedFm.EVENT.END, this.stopListener[id]);
      delete this.stopListener[id];
    }
    if (target instanceof Video && !target.isLooping() && this.stopListener[id]) {
      target.removeEventListener(Video.EVENT.ENDED, this.stopListener[id]);
      delete this.stopListener[id];
    }
    if (target instanceof WaveSurfer && !target.isLooping() && this.stopListener[id]) {
      target.removeEventListener(WaveSurfer.EVENT.FINISH, this.stopListener[id]);
      delete this.stopListener[id];
    }
    target.stop();
    this.mediaList.remove(target);
    this.volumeNormalize();
    return target;
  };

  public isPlaying = (id: string | undefined): boolean => {
    if (!Resource || !id) {
      return false;
    }
    const resource = Resource.get(id);
    if (!resource) {
      return false;
    }
    const target = resource?.object;
    if (!(target instanceof Audio || target instanceof AdaptrFeedFm || target instanceof Video || target instanceof WaveSurfer)) {
      return false;
    }
    if (!this.mediaList.exists(target)) {
      return false;
    }
    return target.isPlaying();
  };

  public pauseAll = (key: string): void => {
    this.globalPause[key] = true;
    this.mediaList.take().forEach((item) => {
      item.pause();
    });
  };

  public resumeAll = (key: string): void => {
    delete this.globalPause[key];
    if (Object.keys(this.globalPause).length > 0) {
      return;
    }
    this.mediaList.take().forEach((item) => {
      if (item.isPlaying()) {
        return;
      }
      item.play();
    });
    this.volumeNormalize();
  };

  public muteAll = (key: string): void => {
    this.globalMute[key] = true;
    this.mediaList.take().forEach((item) => {
      item.mute();
    });
  };

  public unmuteAll = (key: string): void => {
    delete this.globalMute[key];
    if (Object.keys(this.globalMute).length > 0) {
      return;
    }
    this.mediaList.take().forEach((item) => {
      if (!item.isMute()) {
        return;
      }
      item.unMute();
    });
    this.volumeNormalize();
  };

  private volumeNormalize = (): void => {
    const filter = (item: TMedia) => !item.isMute();
    const mediaList = this.mediaList.take(filter);
    if (mediaList.length === 0) {
      return;
    }
    mediaList.forEach((item, key): void => {
      const isLast: boolean = (key === mediaList.length - 1);
      const volume: number = isLast ? 1 : 0.1;
      item.volume(volume);
    });
  };
}

export default new MediaPlayer();
